import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Star } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { QuickViewModal } from '../components/QuickViewModal';
import { OptimizedImage } from '../components/OptimizedImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandPage = () => {
  const { brandSlug } = useParams();
  const [brandData, setBrandData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    const fetchBrand = async () => {
      setLoading(true);
      try {
        const [pageRes, galleryRes] = await Promise.all([
          axios.get(`${API}/brands/${encodeURIComponent(brandSlug)}/page`),
          axios.get(`${API}/brands/${encodeURIComponent(brandSlug)}/review-gallery`).catch(() => ({ data: [] })),
        ]);
        setBrandData(pageRes.data);
        setGallery(galleryRes.data);
      } catch {
        setBrandData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchBrand();
  }, [brandSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F7EB]">
        <div className="h-[400px] bg-[#E5D8D0] animate-pulse" />
        <div className="px-6 md:px-12 lg:px-24 py-12">
          <div className="h-8 bg-[#E5D8D0] w-1/3 mb-4 animate-pulse" />
          <div className="h-4 bg-[#E5D8D0] w-2/3 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!brandData) {
    return (
      <div className="min-h-screen bg-[#F7F7EB] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl mb-4">Brand not found</h1>
          <Link to="/shop" className="text-[#DEA193] hover:underline">Back to shop</Link>
        </div>
      </div>
    );
  }

  const { name, logo_url, hero_image, tagline, story, social_links, products } = brandData;

  // Group products by category
  const grouped = {};
  products.forEach(p => {
    const cat = p.category || 'All';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  });
  const groupEntries = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));

  const hasSocials = social_links && (social_links.instagram || social_links.tiktok || social_links.website);

  return (
    <div className="min-h-screen bg-[#F7F7EB]">
      {/* Hero Banner */}
      <div
        className="relative w-full overflow-hidden"
        style={{ minHeight: hero_image ? '420px' : '280px' }}
        data-testid="brand-hero"
      >
        {hero_image ? (
          <OptimizedImage src={hero_image} alt={name} className="absolute inset-0 w-full h-full object-cover" width={1400} loading="eager" fetchPriority="high" showSkeleton={false} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] to-[#333]" />
        )}
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full min-h-[inherit] px-6 py-16 text-center">
          {logo_url && (
            <OptimizedImage src={logo_url} alt={name} className="h-16 md:h-24 w-auto object-contain mb-6" width={200} showSkeleton={false} data-testid="brand-hero-logo" />
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white font-serif" data-testid="brand-hero-name">
            {name}
          </h1>
          {tagline && (
            <p className="mt-4 text-base md:text-lg text-white/80 max-w-xl tracking-wide" data-testid="brand-hero-tagline">
              {tagline}
            </p>
          )}
          {/* Social Links */}
          {hasSocials && (
            <div className="flex items-center gap-4 mt-6" data-testid="brand-social-links">
              {social_links.instagram && (
                <a href={social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" data-testid="brand-social-instagram" title="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
              )}
              {social_links.tiktok && (
                <a href={social_links.tiktok} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" data-testid="brand-social-tiktok" title="TikTok">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46v-7.15a8.16 8.16 0 005.58 2.18v-3.45a4.85 4.85 0 01-3.58-1.55l2.58-3z"/></svg>
                </a>
              )}
              {social_links.website && (
                <a href={social_links.website} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors text-sm tracking-[0.12em] underline underline-offset-4" data-testid="brand-social-website">
                  Visit Website
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Story Section */}
      {story && (
        <div className="bg-white border-b border-[#E5D8D0]">
          <div className="px-6 md:px-12 lg:px-24 py-12 md:py-16 max-w-3xl mx-auto">
            <div
              className="prose prose-sm md:prose-base text-[#333] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(story) }}
              data-testid="brand-story"
            />
          </div>
        </div>
      )}

      {/* Review Gallery */}
      {gallery.length > 0 && (
        <div className="bg-white border-b border-[#E5D8D0]">
          <div className="px-6 md:px-12 lg:px-24 py-12">
            <h2 className="text-xl tracking-tight mb-6">
              Customer Photos
              <span className="text-sm text-[#999] font-normal ml-2">({gallery.length})</span>
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="review-gallery">
              {gallery.map((item) => (
                <button
                  key={item.image_url}
                  onClick={() => setLightboxImg(item)}
                  className="aspect-square bg-[#F7F7EB] overflow-hidden rounded-sm hover:opacity-80 transition-opacity group relative"
                  data-testid={`gallery-image-${item.image_url}`}
                >
                  <OptimizedImage src={item.image_url} alt={`Review by ${item.reviewer}`} className="w-full h-full object-cover" width={200} showSkeleton={false} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Products Section */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl tracking-tight">
            All Products
            <span className="text-sm text-[#999] font-normal ml-2">({products.length})</span>
          </h2>
        </div>

        {products.length > 0 ? (
          groupEntries.length > 1 ? (
            <div className="space-y-12" data-testid="brand-products-grouped">
              {groupEntries.map(([cat, catProducts]) => (
                <div key={cat}>
                  <h3 className="text-lg font-medium tracking-[0.08em] text-[#000] border-b border-[#E5D8D0] pb-3 mb-6">
                    {cat.replace(/\b\w/g, c => c.toUpperCase())}
                    <span className="text-sm text-[#999] font-normal ml-2">({catProducts.length})</span>
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
                    {catProducts.map((product, i) => (
                      <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${i * 40}ms` }}>
                        <ProductCard product={product} onQuickView={setQuickViewProduct} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="brand-products-grid">
              {products.map((product, i) => (
                <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${i * 40}ms` }}>
                  <ProductCard product={product} onQuickView={setQuickViewProduct} />
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="text-center py-16" data-testid="brand-no-products">
            <p className="text-[#737373] text-lg mb-2">No products yet</p>
            <p className="text-sm text-[#999]">Products will appear here once they are assigned to {name}</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setLightboxImg(null)}>
          <div className="max-w-2xl w-full bg-white rounded-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <img src={lightboxImg.image_url} alt="" className="w-full max-h-[60vh] object-contain bg-[#F7F7EB]" />
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm">{lightboxImg.reviewer}</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`w-3 h-3 ${s <= lightboxImg.rating ? 'text-[#DEA193] fill-[#DEA193]' : 'text-[#E5D8D0]'}`} />
                  ))}
                </div>
              </div>
              {lightboxImg.product_name && <p className="text-xs text-[#999]">{lightboxImg.product_name}</p>}
              {lightboxImg.comment && <p className="text-sm text-[#555] mt-2">{lightboxImg.comment}</p>}
            </div>
            <button onClick={() => setLightboxImg(null)} className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl">&times;</button>
          </div>
        </div>
      )}

      {quickViewProduct && (
        <QuickViewModal product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />
      )}
    </div>
  );
};

export default BrandPage;
